import { h, Fragment, type ComponentChild } from 'preact';
import { Link } from '@tanstack/react-router';
import { renderMessage } from '@goalpost/content';
import { DocsLayout } from '../../components/docs/DocsLayout.tsx';
import { MediaVideo } from '../../components/MediaVideo.tsx';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';
import { useT } from '../../i18n.ts';
import { useLocale } from '../../hooks/useLocale.tsx';

export function DocsApprovalRules() {
  const t = useT();
  const { locale } = useLocale();
  useDocumentTitle(t('docs.approval-rules.title'));

  // The opening paragraph is a shared content key — the canonical one-line
  // description of approval rules. Its {#strong} markup bolds key terms, and the
  // concept markup names ({#workspace}, {#documents}, {#details}, {#teams},
  // {#changes}) resolve to links into the sibling docs pages here; both degrade
  // to plain text wherever the key is reused without this tag map.
  const summary = renderMessage<ComponentChild>('feature.approval-rules.summary', {
    locale,
    tags: {
      workspace: 'workspace',
      documents: 'documents',
      details: 'details',
      teams: 'teams',
      changes: 'changes',
    },
    render: {
      text: (value) => value,
      element: (tag, children) => {
        switch (tag) {
          case 'workspace':
            return <Link to="/docs/workspace">{children}</Link>;
          case 'documents':
            return <Link to="/docs/documents">{children}</Link>;
          case 'details':
            return <Link to="/docs/details">{children}</Link>;
          case 'teams':
            return <Link to="/docs/teams">{children}</Link>;
          case 'changes':
            return <Link to="/docs/change-management">{children}</Link>;
          default:
            return h(tag, null, ...children);
        }
      },
      fragment: (children) => h(Fragment, null, ...children),
    },
  });

  return (
    <DocsLayout
      path="/docs/approval-rules"
      title={t('docs.approval-rules.label')}
      lede="An approval rule decides who has to sign off on a change before it becomes official, and how their approvals combine."
    >
      <MediaVideo
        base="overview-approval-rules-software"
        label="A walkthrough of setting up and applying an approval rule in Goalpost"
      />
      <h2 id="intro">About approval rules</h2>
      <p>{summary}</p>
      <p>
        Approval accumulates across layers. A change must satisfy every rule
        that applies to it: the document’s <strong>baseline</strong> rule (if it
        has one), plus any rule carried by a{' '}
        <Link to="/docs/details">detail</Link> the change touches. If a change
        batch touches a particular detail, then the document-level approval rules
        have to pass (if they’re assigned), as do the approval rules for that
        detail and any of its parents. Each rule in turn resolves down to{' '}
        <strong>team</strong> rules that decide when a given team has signed off.
      </p>
      <p>Within a single rule, approval is decided in two layers:</p>
      <ul>
        <li>
          <strong>Each team’s own rule</strong> decides what counts as that team
          approving: any single member, all members, or a set number of them.
          You set this on the team (see{' '}
          <Link to="/docs/teams" hash="rules">
            Team approval rules
          </Link>
          ).
        </li>
        <li>
          <strong>The rule’s conditions</strong> decide which teams have to
          approve, and how their approvals combine into an overall yes or no.
        </li>
      </ul>
      <p>
        A rule is built from <strong>conditions</strong>. Every condition
        must be satisfied for the rule to pass (an “and” between them). Within
        a single condition you can list alternatives, and any one of them
        satisfies that condition (an “or” within it). A condition can name
        teams or even other rules, so you can reuse a rule as a building
        block inside a larger one.
      </p>

      <h2 id="create">Create an approval rule</h2>
      <p>
        Rules are created in your workspace and given a name, so the same
        policy can be reused across documents and details without rebuilding it
        each time.
      </p>
      <ol>
        <li>
          Open <strong>Approval Rules</strong> in your workspace, then choose{' '}
          <strong>New Rule</strong>.
        </li>
        <li>Give the rule a <strong>Name</strong>.</li>
        <li>
          Choose <strong>Add condition</strong>. Into that condition, use{' '}
          <strong>Add team or rule…</strong> to add the teams (or existing
          rules) that can satisfy it. Add more than one to accept any one of
          them.
        </li>
        <li>
          Add more conditions for anyone who must <em>also</em> sign off. Every
          condition has to be satisfied for the rule to pass.
        </li>
        <li>Choose <strong>Create Rule</strong>.</li>
      </ol>
      <p>
        The builder shows a plain-language summary as you go, joining
        conditions with “and” and alternatives within a condition with “or.”
      </p>

      <h3>Worked examples</h3>
      <p>
        <strong>Solo work, no review.</strong> Leave the document on{' '}
        <strong>None</strong>. Submitting a change makes it official.
      </p>
      <p>
        <strong>Two teams must both sign off.</strong> Add two conditions, one
        naming each team. Because both conditions must be satisfied, both teams
        have to approve. How each team approves is set by that team’s own rule.
      </p>
      <p>
        <strong>Two of three people on one team.</strong> This is a team-level
        rule, not a rule shape. Add one condition naming the team, and set
        that team’s rule to a set number of members (two). Any two of its members
        can then approve on the team’s behalf.
      </p>
      <p>
        <strong>Either of two teams can cover.</strong> Add a single condition
        and put both teams in it as alternatives. Approval from either one
        satisfies the condition.
      </p>

      <h2 id="apply-document">Apply a rule to a document</h2>
      <p>Applying a rule to a document governs changes to that document.</p>
      <ol>
        <li>
          Go to your document and select <strong>Approval Rules</strong> from
          the document nav.
        </li>
        <li>
          Open the <strong>Document approval rule</strong> menu.
        </li>
        <li>
          Choose a rule, or <strong>None — no approval required</strong>.
        </li>
        <li>Choose <strong>Set</strong>.</li>
      </ol>
      <p>
        The rule applies to every change submitted from that point forward.
        Changes already waiting for approval keep the rule that was in force when
        they were submitted (see below).
      </p>

      <h3>Changing which rule a document uses</h3>
      <p>
        You can change a document’s rule at any time. What happens to in-flight
        work depends on its state:
      </p>
      <ul>
        <li>
          <strong>Drafts</strong> are unaffected. They use the document’s current
          rule when you submit them.
        </li>
        <li>
          <strong>Changes waiting for approval</strong> continue under the
          rule that was in force when they were submitted, and existing
          approvals carry over. To re-evaluate one under the new rule, withdraw it
          (or have an approver reject it) and resubmit.
        </li>
        <li>
          <strong>Approved versions</strong> are unaffected. Each one records the
          rule that approved it, permanently.
        </li>
      </ul>

      <h2 id="apply-detail">Apply a rule to a detail</h2>
      <p>
        Some sections of a document deserve stricter sign-off than others, such
        as a budget, a legal clause, or a decision everything else depends on. You can
        apply an approval rule to an individual{' '}
        <Link to="/docs/details">detail</Link>, so that any change touching that
        detail (or anything beneath it) has to satisfy that rule on top of the
        document’s own.
      </p>
      <p>
        You set this while proposing a change: each detail has a{' '}
        <strong>Set approval rule</strong> action that lets you pick one of
        the document’s rules, or clear it back to <strong>None</strong>. The
        rule’s teams must be assigned to the document. Full steps are under{' '}
        <Link to="/docs/details" hash="approval">
          Set an approval rule
        </Link>{' '}
        on the Details page.
      </p>

      <h2 id="edit">Edit an approval rule</h2>
      <p>
        Because rules are named and reusable, you edit one in a single place
        and every document and detail using it picks up the change. Open{' '}
        <strong>Approval Rules</strong> in your workspace, choose{' '}
        <strong>Edit</strong> on the rule, and adjust its conditions.
      </p>
      <p>
        Edits apply to changes submitted after you save. Changes already waiting
        for approval continue under the earlier version of the rule, and
        approved versions keep the exact rule that approved them, so editing a
        rule never rewrites history.
      </p>
      <p>
        A rule that a document or detail still relies on can be{' '}
        <strong>archived</strong> (retired from new use) but not deleted, which
        keeps history intact. A rule that has never been used can be deleted
        outright.
      </p>

      <h3>What the history shows</h3>
      <p>
        When a change is approved, Goalpost records the rule that approved it
        alongside the change: the teams that participated, each team’s rule at the
        time, and who approved on each team’s behalf. That record stays put even
        if you later rename teams or rewrite the rule, so a document’s history
        always reflects the policy that was actually in force. That is exactly
        what an auditor needs. You can even add an external reviewer as a team
        with no approval authority so they can read the full history without being
        able to change anything.
      </p>
    </DocsLayout>
  );
}
